import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import ActionPlate from 'src/components/ui/ActionPlate';

import { DropzoneWrapper } from './styled/DropzoneWrapper';
import sx from './styled/sx';
import { FileOutlined } from '@ant-design/icons';
import { humanFileSize } from './helpers';

const FileUpload = ({ error, value, onChange, multiple }) => {
	const [fileState, setFileState] = useState(null);
	const file = value === undefined ? fileState : value;

	const handleDrop = useCallback(
		(acceptedFiles) => {
			const setFunc = onChange || setFileState;
			setFunc(
				acceptedFiles.map((file) => {
					const isImg = /^image\//.test(file.type);
					if (!isImg) return file;
					return Object.assign(file, {
						preview: URL.createObjectURL(file),
					});
				}),
			);
		},
		[onChange],
	);

	const handleDelete = useCallback(() => {
		const setFunc = onChange || setFileState;
		setFunc(null);
	}, [onChange]);

	const { getRootProps, getInputProps, isDragActive, isDragReject } =
		useDropzone({
			multiple,
			onDrop: handleDrop,
		});

	const isUploadable = multiple || !file?.length;

	return (
		<Stack spacing={1} sx={{ width: '100%', position: 'relative' }}>
			{isUploadable && (
				<DropzoneWrapper
					{...getRootProps()}
					sx={sx.wrap({ isDragActive, isDragReject, error })}
				>
					<input {...getInputProps()} />
					{!file?.length && (
						<Stack height={'100%'} justifyContent="center" alignItems="center">
							<Typography color="secondary">
								Перетащите файл для загрузки
							</Typography>
							<Typography color="secondary">кликните для выбора</Typography>
						</Stack>
					)}
				</DropzoneWrapper>
			)}
			{(file || []).map((item) => (
				<ActionPlate key={item.name} isGrey onDelete={() => handleDelete()}>
					<Stack
						direction="row"
						spacing={4}
						alignItems="center"
						sx={{ height: 96 }}
					>
						{item.preview && (
							<Box
								component="img"
								src={item.preview}
								sx={sx.fileIcon()}
								onLoad={() => {
									URL.revokeObjectURL(item.preview);
								}}
							/>
						)}
						{!item.preview && (
							<Paper sx={sx.fileIcon()}>
								<Box>
									<FileOutlined />
								</Box>
							</Paper>
						)}
						<Stack>
							<Typography color="secondary" variant="caption">
								Файл
							</Typography>
							<Typography variant="subtitle1">{item.name}</Typography>
							<Typography variant="body2">
								{humanFileSize(item.size)}
							</Typography>
						</Stack>
					</Stack>
				</ActionPlate>
			))}
		</Stack>
	);
};

export const FileShape = {
	name: PropTypes.string,
	size: PropTypes.number,
	preview: PropTypes.string, // optional src url for images
};

FileUpload.propTypes = {
	error: PropTypes.string,
	value: PropTypes.arrayOf(PropTypes.shape(FileShape)),
	onChange: PropTypes.func,
	multiple: PropTypes.bool,
};

export default FileUpload;
